// Centralised, public-safe brand + navigation config.
// Do NOT hardcode business-critical values in components. Import from here.

export const COMPANY = {
  name: "Minga Architecture",
  legalName: "Minga Architecture PLLC",
  tagline: "Residential Design and BIM Production",
  email: "info@mingaarchitecture.com",
  phone: null,
  whatsapp: null,
  address: null,
  linkedinUrl: null,
};

export const NAV_PUBLIC = [
  { label: "About Us", href: "/about" },
  { label: "Residential Design", href: "/residential-design" },
  { label: "BIM Production", href: "/bim-production" },
  { label: "Contact Us", href: "/contact" },
];

export const ASSETS = {
  logo: "/assets/brand/minga-logo.png",
  patternIcon: "/assets/brand/minga-pattern-icon.png",
  emprenovus:
    "https://customer-assets.emergentagent.com/job_72f2e356-3a43-43f8-bc33-2c9ea7932b6a/artifacts/eq1perie_Emprenovus.png",
};

// Local team media with B/W + color pairs.
// Homepage uses portrait + hoverImage for B/W to color hover.
// About Us uses only the color image.
const HUGO_GALLERY = Array.from({ length: 24 }, (_, i) => {
  const n = String(i + 1).padStart(2, "0");

  return {
    bw: `/assets/minga/team/hugo/gallery/hugo-${n}-bw.jpg`,
    color: `/assets/minga/team/hugo/gallery/hugo-${n}-color.jpg`,
  };
});

const CHRISTIAN_GALLERY = Array.from({ length: 7 }, (_, i) => {
  const n = String(i + 1).padStart(2, "0");

  return {
    bw: `/assets/minga/team/christian/gallery/christian-${n}-bw.jpg`,
    color: `/assets/minga/team/christian/gallery/christian-${n}-color.jpg`,
  };
});

export const TEAM_MEDIA = {
  hugo: {
    portrait: HUGO_GALLERY[0].bw,
    hoverImage: HUGO_GALLERY[0].color,
    gallery: HUGO_GALLERY,
  },
  christian: {
    portrait: CHRISTIAN_GALLERY[0].bw,
    hoverImage: CHRISTIAN_GALLERY[0].color,
    gallery: CHRISTIAN_GALLERY,
  },
};

export function getTeamMedia(member = {}) {
  const name = (member.name || "").toLowerCase();

  const local = name.includes("hugo")
    ? TEAM_MEDIA.hugo
    : name.includes("christian") || name.includes("cristian")
      ? TEAM_MEDIA.christian
      : {};

  const gallery = member.gallery?.length ? member.gallery : local.gallery || [];

  return {
    portrait: member.portrait || local.portrait,
    hoverImage:
      member.hover_image ||
      member.hoverImage ||
      member.color ||
      local.hoverImage ||
      member.portrait ||
      local.portrait,
    gallery,
  };
}
export const FALLBACK_TEAM = [
  {
    id: "fallback-hugo",
    name: "Hugo Carrión Berru, RA, AIA",
    role: "Founder / Architect",
    credentials: "Licensed Architect in Washington · RA · AIA",
    short_bio:
      "Hugo is a licensed architect in the state of Washington. He brings a personal approach to residential architecture, combining listening, technical experience and collaboration to create unique, welcoming spaces.",
    full_bio:
      "Hugo is a licensed architect in the state of Washington. He brings a personal approach to residential architecture, combining listening, technical experience and collaboration to create unique, welcoming spaces. His work connects the collective spirit of Minga with a precise understanding of construction, design coordination and the personal stories behind each home.",
    portrait: TEAM_MEDIA.hugo.portrait,
    hover_image: TEAM_MEDIA.hugo.hoverImage,
    gallery: TEAM_MEDIA.hugo.gallery,
  },
  {
    id: "fallback-christian",
    name: "Christian Sol",
    role: "Architect / BIM Modeler",
    credentials: "Architect, BIM Modeler",
    short_bio:
      "Christian is an architect and BIM modeler focused on thoughtful residential design, BIM coordination and project development. He graduated from Pontificia Universidad Católica del Ecuador and has continued specializing in BIM Modeling and Management with Revit.",
    full_bio: `Christian is an architect and BIM modeler passionate about thoughtful residential design and the impact architecture can have on the urban environment. He believes every project, from a single-family home to a multifamily development, should contribute positively to the city and the community around it. With a strong attention to detail and a collaborative mindset, Christian supports the design process through advanced BIM modeling, coordination, and project development.

Graduated from Pontificia Universidad Católica del Ecuador with a Bachelor’s Degree in Architecture and later earning a Master’s Degree in Urbanism from the same institution, Christian has continued expanding his expertise through specialization studies in BIM Modeling and Management with Revit. He is currently pursuing further specialization in BIM visual programming and automation, focused on improving workflows, efficiency, and project coordination.

Originally from Ambato, Ecuador, Christian combines technical precision with a deep appreciation for family life, continuous learning, and meaningful design. His work is driven by the belief that architecture should create functional, welcoming, and sustainable spaces that enrich everyday living.`,
    portrait: TEAM_MEDIA.christian.portrait,
    hover_image: TEAM_MEDIA.christian.hoverImage,
    gallery: TEAM_MEDIA.christian.gallery,
  },
];

export const FALLBACK_TESTIMONIALS = [
  {
    id: "fallback-espitia",
    client_name: "Alejandro and Jill Espitia",
    location: "Brier, Washington",
    featured: true,
    quote:
      "We were amazed and impressed by how Hugo was able to capture exactly what we had described for our renovation. Working with Hugo was a delight as he was always pleasant and very patient. He guided us to what could work on our addition, and helped us understand the process. His design worked perfectly for the adaptation to our existing structure. We couldn’t have been happier with the end result. We absolutely love our new space! Thank you Hugo for your professionalism.",
  },
  {
    id: "fallback-tynan",
    client_name: "Joe Tynan",
    location: "Everett, Washington",
    quote:
      "Working with Minga Architects on my carport-to-garage conversion project was an excellent experience. Hugo Carrion Berru was incredibly helpful throughout the entire process, offering thoughtful input on my ideas while also keeping the project realistic and achievable. He played a key role in making sure all permits were completed correctly and coordinated every prerequisite needed for the project, including the engineer and surveyor. Hugo was always prompt, professional, and responsive to any follow-up questions I had. I truly appreciated his guidance and would highly recommend Minga Architecture to anyone!",
  },
  {
    id: "fallback-fonder-reeve",
    client_name: "Ro Fonder-Reeve",
    location: "Seattle, Washington",
    quote:
      "I worked with Hugo on a project that began when a huge maple tree fell on my house, necessitating some major repairs. From our first meeting, I felt that not only is he personable, but that we shared the values of community and equity. Over the course of the 9-month (so far) project, we have navigated complex permitting issues including replacing an existing porch on a property on a steep hill that is in an Environmentally Critical Area. Hugo helped me navigate the confusing and challenging city requirements in a way that met the City's requirements and helped to keep my project within a budget I could afford. Even more amazing, he managed to do some of the work while he was in Ecuador and still provide me with a seamless experience. I truly cannot say enough good things about my work with Minga. They are a wonderful resource to have in the community.",
  },
  {
    id: "fallback-cyr",
    client_name: "Christy and Tod Cyr",
    location: "West Seattle, Washington",
    related_project: "1917-bungalow-remodel",
    quote:
      "Hugo Carrion-Berru of Minga Architecture has been an absolute pleasure to work with on the renovation of our 1917 bungalow. His professionalism stood out from day one. He communicated clearly and promptly, kept us informed at every stage, was transparent regarding billing, and never left us guessing about next steps. What really impressed us was his ability to take our scattered, sometimes contradictory lists of wants, needs, and pain points and translate them into a cohesive, elegant design that actually makes sense as a whole. He has a real gift for seeing the bigger picture and finding solutions that satisfy multiple goals at once. We'd recommend Hugo without hesitation to anyone tackling a renovation, especially in an older home where unexpected issues are part of the territory.",
  },
];
